/**
 * Snowflake ids as the browser reads them — the mirror of App\Services\
 * IdGenerator and config/id.php.
 *
 * An id is `{prefix}_{16 hex}`. The hex is a 64-bit value laid out as
 * [41 bits: ms since the epoch][10 bits: worker][12 bits: sequence], so the
 * creation time can be read straight off the id, and the prefix says what
 * kind of record it names.
 */

/** config/id.php `epoch_ms` — 2025-01-01T00:00:00Z. */
const EPOCH_MS = 1735689600000n;

/** config/id.php `bits.worker + bits.seq`: what sits below the timestamp. */
const LOW_BITS = 22n;

/**
 * How many hex digits a truncated id keeps. Ten digits pin the creation time
 * to ~4ms; eight (~1s) made passports created in one batch look identical,
 * and the tail (worker + sequence, e.g. "…7000") is shared by most ids.
 */
export const TRUNCATED_HEX = 10;

const SNOWFLAKE = /^([a-z]+)_([0-9a-f]{16})$/;

/** Every prefix IdGenerator is called with, named for people. */
export const SNOWFLAKE_TYPES = {
    pass: 'Passport',
    ontology: 'Ontology',
    graph: 'Ontology graph',
    property: 'Property',
    template: 'Template',
    chg: 'Change',
    import: 'Import',
    file: 'File',
    evt: 'Event',
    rel: 'Release',
    dreq: 'Data request',
    dset: 'Request set',
    dsu: 'Set upload',
    dsj: 'Set job',
    sup: 'Supplier',
    xfer: 'Transfer',
};

export function isSnowflake(value) {
    return typeof value === 'string' && SNOWFLAKE.test(value);
}

/**
 * The parts of an id. Anything that is not a snowflake still parses — as an
 * opaque id with no prefix, type "ID" and no creation time — so a caller can
 * hand over whatever it has (a request UUID, a legacy key) and get a sane
 * rendering.
 *
 * @returns {{ id: string, prefix: string|null, body: string, type: string, createdAt: Date|null }}
 */
export function parseSnowflake(value) {
    const id = String(value ?? '');
    const match = SNOWFLAKE.exec(id);
    if (!match) {
        return { id, prefix: null, body: id, type: 'ID', createdAt: null };
    }
    const [, prefix, body] = match;
    let createdAt = null;
    try {
        createdAt = new Date(Number((BigInt(`0x${body}`) >> LOW_BITS) + EPOCH_MS));
    } catch {
        createdAt = null;
    }

    return { id, prefix, body, type: SNOWFLAKE_TYPES[prefix] ?? 'Snowflake', createdAt };
}

/** The shown body: whole, or its time-ordered head. */
export function shownBody(parsed, truncate) {
    if (!truncate || !parsed.prefix || parsed.body.length <= TRUNCATED_HEX) {
        return parsed.body;
    }
    return `${parsed.body.slice(0, TRUNCATED_HEX)}…`;
}

/** "Sep 8, 2026, 21:14:41 UTC" — the instant is absolute, so say which zone. */
export function formatCreatedAt(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    return `${date.toLocaleString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    })} UTC`;
}
